/**
 * 65.49: the anti-dropship missile's looks — a shoulder-fired anti-air launcher and its guided missile, for the owner to choose between
 * (design_sheets/anti_dropship_missile.html). The owner, Oct 20: "I think it's time to introduce a stinger missile (mostly to introduce
 * guided missile code, and to test out aa gameplay). I think it should be relatively short range, and possible to beat in
 * some cases with maneuver". Lane F builds the sim (the guided-missile core, the weapon); this is the look only.
 *
 * - **The launcher** in three options: A (a fibreglass tube and its caps, a grip stock with its battery
 *   pack plugged in under the front, the folding sight frame on the head's side, a grid antenna standing
 *   up off the front); B (a slimmer tube flaring to a bell at the front, the battery forward under the tube,
 *   a ring sight on top, a blade antenna); C our own, an orbital company's (a six-sided tube, the optic block with its
 *   screen, a battery cassette, a fore-grip, a flat panel antenna). Each is a `LawModel` (the tube's slot on the person):
 *   the front sight and the antenna fold down as it is carried and stand up as it comes to the shoulder
 *   (`setLawExtended`: the sights, and the antenna as one of its `folds`), the strong hand on the grip stock's pistol grip,
 *   the support hand under the front.
 * - **The anchors**, by the rocket tube's names (`LawModel`; the tube's frame — z along the bore, y up, x the strong side
 *   as built): `muzzle` the tube's front, where the missile leaves (`PersonView.muzzleWorld` reads it, as the tube's);
 *   `rearSight` the sight the eye looks through (A's peep, B's rear notch, C's optic's screen), the eye just behind it;
 *   `frontSight` the sight ahead of it (A's frame, B's ring; C's optic's objective); the anti-dropship missile's own `breech`, the
 *   tube's rear, the back-blast's, and `antenna`. The group's origin is `ANTI_DROPSHIP_MISSILE.shoulder` ahead of the shoulder it rests
 *   on. `ANTI_DROPSHIP_MISSILE_LAUNCH` is the muzzle from the person's feet for the sim's launch point.
 * - **The lock** shown on the launcher: the lamp at the sight's eye (`setAntiDropshipMissileLock`: dark, a dim search, an amber track
 *   that blinks, a steady green lock). The tone is sound's.
 * - **The missile** (`buildMissile`, one per option): the seeker's dome, the canards, the bands, the tail fins that spring
 *   out after the eject, the motor's flame and a glow point that reads at any range; `poseMissile` from its age through
 *   `ANTI_DROPSHIP_MISSILE_FLIGHT` — ejected by the launch motor inside the tube, a short coast clear of the gunner, then the flight
 *   motor lights.
 * - **The smoke**: `MissileTrail`, a thin ribbon facing the camera that widens and fades (one draw a missile), or
 *   `PuffField`, puffs (one draw for every puff in the scene) — the back-blast's and the eject's too.
 *
 * Person units (a person is 1.8). Rendering only: no game state is read or written.
 */
import * as THREE from 'three';
import { bakeStaticParts, markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';
import { setLawExtended, type LawModel } from './groundWeapons';
import { GUN_PRIMS } from './gunLooks';

const { box, cyl, slab, plate, at } = GUN_PRIMS;

export type AntiDropshipMissileOption = 'A' | 'B' | 'C';
export const ANTI_DROPSHIP_MISSILE_OPTIONS: Record<AntiDropshipMissileOption, string> = {
  A: 'A: the grid antenna, the sight frame, the battery under the front',
  B: 'B: the bell at the front, the battery forward, a ring sight',
  C: 'C, our own: a six-sided tube, the optic block and its screen, the fore-grip (the owner\'s pick)',
};
/**
 * The owner's picks on the sheet (Oct 20: *"Ok for stinger, I like hud view B, launcher C"*): what play draws when 65.49's
 * weapon is on — launcher C (`buildAntiDropshipMissile`'s and `buildMissile`'s default) and HUD view B (ui/antiDropshipMissileHud.ts
 * `drawAntiDropshipMissileHud`: the seeker's circle, its cross, the diamond at the lock). The sheet keeps the others beside them.
 */
export const ANTI_DROPSHIP_MISSILE_PICK = { launcher: 'C', hud: 'B' } as const satisfies { launcher: AntiDropshipMissileOption; hud: 'A' | 'B' | 'C' };

/** Each option's own size (B a little longer and slimmer than A, C shorter and fatter). */
const SIZE: Record<AntiDropshipMissileOption, { length: number; radius: number }> = { A: { length: 1.62, radius: 0.06 }, B: { length: 1.68, radius: 0.052 }, C: { length: 1.5, radius: 0.066 } };
/**
 * The launcher's size, the pick's (C): `length` end to end, `radius` the tube's (about the LAW's doubled diameter, DECISIONS
 * on #995, kept for the family), `shoulder` how far the group's origin (the tube's middle) is ahead of where it rests on
 * the shoulder — the tube's slot on the person puts its origin 0.12 ahead of the shoulder (personModel HAND_POSE.rocket),
 * so the tube rests 0.12 behind its middle: 0.63 behind the shoulder, 0.87 ahead.
 */
export const ANTI_DROPSHIP_MISSILE = { ...SIZE[ANTI_DROPSHIP_MISSILE_PICK.launcher], shoulder: 0.12 } as const;

/**
 * Where the missile leaves, for the sim's launch point (person units, measured off the posed figure; a right-hander, the
 * left-hander's `right` negated; tests/antiDropshipMissileLooks.test.ts holds it). The tube turns with the aim about the shoulder's
 * pivot — `pivot` from the feet (`up`, to the strong side `right`, along the facing `ahead`), standing or crouched — and
 * the muzzle is `bore[option]` along the aim from it and `lift` off it, square to the aim and upward (the tube resting on
 * the shoulder). So, aimed along the unit vector `aim` with `aimUp` square to it in its vertical plane:
 * `muzzle = feet + up·pivot.up + right·pivot.right + forward·pivot.ahead + aim·bore + aimUp·lift`.
 * Standing level that is 1.58 up, 0.21 right, 0.81 ahead (A). The drawn missile blends from the drawn muzzle to the
 * sim's over the eject, so a sim that launches from its eye height still reads.
 */
export const ANTI_DROPSHIP_MISSILE_LAUNCH = {
  pivot: { stand: { up: 1.512, right: 0.213, ahead: -0.131 }, crouch: { up: 0.978, right: 0.213, ahead: 0.113 } },   // (73.3: the lower, hunched kneel; was up 1.143, ahead −0.082)
  lift: 0.07,
  bore: { A: 0.94, B: 0.97, C: 0.88 } as Record<AntiDropshipMissileOption, number>,
} as const;

/** The tube's far tier: one bar on the shoulder (as GUN_FAR's), the pick's length and grey (C), the grip stock a box under it. */
export const ANTI_DROPSHIP_MISSILE_FAR = { length: ANTI_DROPSHIP_MISSILE.length, thick: 0.13, color: '#686d6e', stock: { w: 0.07, h: 0.14, d: 0.22, color: '#3a3e40' } } as const;

export interface AntiDropshipMissileModel extends LawModel {
  option: AntiDropshipMissileOption;
  /** the tube's rear: the back-blast leaves here */
  breech: THREE.Object3D;
  /** the IFF antenna on its hinge (one of the tube's `folds`: flat as it is carried) */
  antenna: THREE.Group;
  /** the lock's lamp at the eye, its own material (`setAntiDropshipMissileLock`) */
  lamp: THREE.Mesh;
}

/** The lock's states as the launcher shows them (the seeker's: off, uncaged and searching, tracking a target, locked). */
export type LockState = 'off' | 'search' | 'track' | 'lock';
/** The lamp's colours, and the track's blink (seconds a cycle). */
export const LOCK_LAMP = { off: '#1d201b', search: '#6a5326', track: '#ffae38', lock: '#6dff74', blink: 0.25 } as const;

const OLIVE = '#5b6044', CAP = '#2a2c24', STOCK = '#363832', BCU = '#7c776a', SIGHT = '#2b2d29';

function lampMesh(parent: THREE.Object3D, w: number, h: number, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.006), new THREE.MeshBasicMaterial({ color: LOCK_LAMP.off }));
  m.position.set(x, y, z); parent.add(m);
  return m;
}
/**
 * The tube's slot's empty parts: nothing slides or opens on these (`setLawExtended` moves only the front sight and the
 * antenna; the rear sight is the eye's anchor, nothing on it folds).
 */
function finish(g: THREE.Group, option: AntiDropshipMissileOption, o: { muzzle: THREE.Object3D; breech: THREE.Object3D; gripR: THREE.Object3D; gripL: THREE.Object3D; frontSight: THREE.Group; rearSight: THREE.Group; antenna: THREE.Group; lamp: THREE.Mesh }, bake: boolean): AntiDropshipMissileModel {
  const inner = new THREE.Group(), frontCover = new THREE.Group(), rearCover = new THREE.Group();
  g.add(inner, frontCover, rearCover);
  const m: AntiDropshipMissileModel = { group: g, option, muzzle: o.muzzle, breech: o.breech, gripR: o.gripR, gripL: o.gripL, magazine: inner, well: new THREE.Vector3(), inner,
    frontSight: o.frontSight, rearSight: o.rearSight, frontCover, rearCover, folds: [o.antenna], antenna: o.antenna, lamp: o.lamp };
  setAntiDropshipMissileRaised(m, 0);
  if (bake) bakeStaticParts(g, { exclude: [o.frontSight, o.rearSight, o.antenna, o.lamp] });
  return m;
}
/** The rear sight: an empty anchor at the sight the eye looks through (the eye just behind it). */
function rearAt(g: THREE.Group, x: number, y: number, z: number): THREE.Group { const r = new THREE.Group(); r.position.set(x, y, z); g.add(r); return r; }

/** A: the fibreglass tube, the grip stock, the sight frame, the grid antenna. */
function antiDropshipMissileA(p: UnitPalette, bake: boolean): AntiDropshipMissileModel {
  const g = new THREE.Group(), { length, radius: r } = SIZE.A, half = length / 2, sh = -ANTI_DROPSHIP_MISSILE.shoulder;
  cyl(g, r, length - 0.12, OLIVE, 0, 0, 0, 10);                                                               // the launch tube
  cyl(g, r + 0.013, 0.07, CAP, 0, 0, half - 0.035, 10);                                                      // the front cap (the missile breaks it)
  cyl(g, r + 0.013, 0.06, CAP, 0, 0, -half + 0.03, 10);                                                      // the rear cap (blown off)
  cyl(g, r + 0.004, 0.03, p.flash, 0, 0, half - 0.16, 10, r + 0.004, true);                                  // a team band
  cyl(g, r + 0.004, 0.02, '#b89a3a', 0, 0, -half + 0.12, 10, r + 0.004, true);                               // the live round's yellow band
  // the grip stock under the tube, ahead of the shoulder: its body, the pistol grip raked back, the guard; the shoulder rest behind it
  slab(g, [sh + 0.08, -r + 0.01, sh + 0.46, -r + 0.01, sh + 0.46, -r - 0.045, sh + 0.36, -r - 0.07, sh + 0.14, -r - 0.07, sh + 0.08, -r - 0.04], 0.07, STOCK, 0, 0.006);
  slab(g, [sh + 0.26, -r - 0.06, sh + 0.32, -r - 0.06, sh + 0.27, -r - 0.24, sh + 0.21, -r - 0.24], 0.05, STOCK, 0, 0.006);
  slab(g, [sh + 0.33, -r - 0.07, sh + 0.37, -r - 0.07, sh + 0.37, -r - 0.12, sh + 0.3, -r - 0.17, sh + 0.28, -r - 0.15, sh + 0.34, -r - 0.11], 0.02, STOCK);
  slab(g, [sh - 0.12, -r + 0.01, sh + 0.08, -r + 0.01, sh + 0.06, -r - 0.035, sh - 0.1, -r - 0.035], 0.09, CAP, 0, 0.008);   // the shoulder rest
  box(g, 0.03, 0.05, 0.08, STOCK, -0.045, -r - 0.03, sh + 0.2);                                              // the IFF interrogator's box on the stock
  const bcu = cyl(g, 0.026, 0.15, BCU, 0, -r - 0.11, sh + 0.52, 8); bcu.rotation.x = Math.PI / 2 + 0.85;     // the battery coolant unit, plugged in down and forward
  // the sight out on the head's side: the arm and the rail, the peep at the rear with the lamp over it (`rearSight` at its
  // aperture), and the frame (`frontSight`: folds back flat onto the rail) with its reticle
  const sx = -r - 0.1, sy = -0.02;
  box(g, 0.1, 0.016, 0.024, SIGHT, -r - 0.05, sy, sh + 0.2);                                                 // the arm
  box(g, 0.008, 0.008, 0.26, SIGHT, sx, sy, sh + 0.2);                                                       // the rail
  cyl(g, 0.016, 0.03, SIGHT, sx, sy + 0.05, sh + 0.08, 8, 0.016, true);                                      // the peep
  box(g, 0.006, 0.05, 0.008, SIGHT, sx, sy + 0.022, sh + 0.08);                                              //   its post
  const lamp = lampMesh(g, 0.014, 0.01, sx, sy + 0.074, sh + 0.066);
  const rearSight = rearAt(g, sx, sy + 0.05, sh + 0.08);
  const sight = new THREE.Group(); sight.position.set(sx, sy, sh + 0.32); g.add(sight);
  plate(sight, [-0.045, 0, 0.045, 0, 0.045, 0.1, -0.045, 0.1], 0.008, SIGHT, 0, [[-0.037, 0.008, 0.037, 0.008, 0.037, 0.092, -0.037, 0.092]]);
  box(sight, 0.074, 0.003, 0.004, SIGHT, 0, 0.05, 0); box(sight, 0.003, 0.084, 0.004, SIGHT, 0, 0.05, 0);   // the reticle's cross
  // the IFF antenna (one of the `folds`: lies back on the tube, stands up): the grid on its hinge, leant out to the head's side
  const antenna = new THREE.Group(); antenna.position.set(-r * 0.6, r * 0.8, half - 0.34); g.add(antenna);
  const lean = new THREE.Group(); lean.rotation.z = 0.45; antenna.add(lean);
  plate(lean, [-0.06, 0, 0.06, 0, 0.06, 0.13, -0.06, 0.13], 0.006, SIGHT, 0,
    [0, 1].flatMap(c => [0, 1, 2].map(rw => { const x0 = -0.052 + c * 0.056, y0 = 0.008 + rw * 0.041; return [x0, y0, x0 + 0.048, y0, x0 + 0.048, y0 + 0.033, x0, y0 + 0.033]; })));
  const muzzle = at(g, 0, 0, half + 0.01), breech = at(g, 0, 0, -half - 0.01);
  const gripR = at(g, 0, -r - 0.16, sh + 0.27), gripL = at(g, -0.01, -r - 0.02, sh + 0.72);
  return finish(g, 'A', { muzzle, breech, gripR, gripL, frontSight: sight, rearSight, antenna, lamp }, bake);
}

/** B: the slim tube flaring to a bell, the ring sight, the blade antenna. */
function antiDropshipMissileB(p: UnitPalette, bake: boolean): AntiDropshipMissileModel {
  const g = new THREE.Group(), { length, radius: r } = SIZE.B, half = length / 2, sh = -ANTI_DROPSHIP_MISSILE.shoulder, G = '#4f5c3b', GD = '#3d482d';
  cyl(g, r, length - 0.3, G, 0, 0, -0.12, 10);                                                               // the tube
  cyl(g, r, 0.22, GD, 0, 0, half - 0.15, 10, r + 0.022);                                                     // the bell at the front, flaring
  cyl(g, r + 0.024, 0.04, CAP, 0, 0, half - 0.02, 10);                                                       //   its cap
  cyl(g, r + 0.01, 0.05, CAP, 0, 0, -half + 0.025, 10);                                                      // the rear ring
  cyl(g, r + 0.004, 0.035, p.flash, 0, 0, -half + 0.16, 10, r + 0.004, true);                                // a team band
  for (const z of [-0.3, 0.1]) cyl(g, r + 0.006, 0.014, GD, 0, 0, z, 10, r + 0.006, true);                   // the straps' bands
  // the grip stock: a deep body, the grip straight down, the trigger guard; the battery (the ground power source) forward under the tube
  slab(g, [sh + 0.1, -r + 0.01, sh + 0.42, -r + 0.01, sh + 0.42, -r - 0.06, sh + 0.1, -r - 0.06], 0.07, STOCK, 0, 0.008);
  slab(g, [sh + 0.22, -r - 0.05, sh + 0.29, -r - 0.05, sh + 0.27, -r - 0.23, sh + 0.2, -r - 0.23], 0.05, STOCK, 0, 0.006);
  slab(g, [sh + 0.3, -r - 0.06, sh + 0.34, -r - 0.06, sh + 0.34, -r - 0.13, sh + 0.28, -r - 0.16, sh + 0.27, -r - 0.14, sh + 0.31, -r - 0.1], 0.02, STOCK);
  cyl(g, 0.03, 0.22, BCU, 0, -r - 0.045, sh + 0.55, 8);                                                       // the battery, forward
  cyl(g, 0.034, 0.02, CAP, 0, -r - 0.045, sh + 0.67, 8);                                                     //   its cap
  // the sight on top, to the head's side: the rear notch on its post with the lamp (`rearSight` at the notch), and the front
  // ring (`frontSight`: folds back flat)
  const sx = -r * 0.75, sy = r * 0.66;
  box(g, 0.012, 0.05, 0.012, SIGHT, sx, sy + 0.025, sh + 0.1);
  plate(g, [sx - 0.02, sy + 0.04, sx + 0.02, sy + 0.04, sx + 0.02, sy + 0.07, sx - 0.02, sy + 0.07], 0.01, SIGHT, sh + 0.1, [[sx - 0.008, sy + 0.048, sx + 0.008, sy + 0.048, sx + 0.008, sy + 0.062, sx - 0.008, sy + 0.062]]);
  const lamp = lampMesh(g, 0.012, 0.01, sx, sy + 0.078, sh + 0.093);
  const rearSight = rearAt(g, sx, sy + 0.055, sh + 0.1);
  const sight = new THREE.Group(); sight.position.set(sx, sy, sh + 0.44); g.add(sight);
  box(sight, 0.01, 0.05, 0.01, SIGHT, 0, 0.025, 0);                                                          // the front post
  plate(sight, [-0.04, 0.035, 0.04, 0.035, 0.04, 0.095, -0.04, 0.095], 0.006, SIGHT, 0, [[-0.031, 0.043, 0.031, 0.043, 0.031, 0.087, -0.031, 0.087]]);   // its ring (square)
  // the antenna (one of the `folds`): a blade on a hinge at the bell, leant out
  const antenna = new THREE.Group(); antenna.position.set(-r * 0.7, r * 0.7, half - 0.36); g.add(antenna);
  const lean = new THREE.Group(); lean.rotation.z = 0.5; antenna.add(lean);
  slab(lean, [-0.02, 0, 0.03, 0, 0.012, 0.2, -0.008, 0.2], 0.008, SIGHT);
  const muzzle = at(g, 0, 0, half + 0.01), breech = at(g, 0, 0, -half - 0.01);
  const gripR = at(g, 0, -r - 0.15, sh + 0.25), gripL = at(g, -0.01, -r - 0.02, sh + 0.75);
  return finish(g, 'B', { muzzle, breech, gripR, gripL, frontSight: sight, rearSight, antenna, lamp }, bake);
}

/** C: our own — an orbital company's launcher. */
function antiDropshipMissileC(p: UnitPalette, bake: boolean): AntiDropshipMissileModel {
  const g = new THREE.Group(), { length, radius: r } = SIZE.C, half = length / 2, sh = -ANTI_DROPSHIP_MISSILE.shoulder, H = '#686d6e', HD = '#3a3e40';
  cyl(g, r, length - 0.1, H, 0, 0, 0, 6);                                                                   // the six-sided tube
  cyl(g, r + 0.012, 0.05, HD, 0, 0, half - 0.025, 6);                                                        // the front collar
  cyl(g, r + 0.016, 0.07, HD, 0, 0, -half + 0.035, 6);                                                       // the rear collar
  for (const z of [half - 0.2, -half + 0.2]) box(g, 0.02, 0.01, 0.12, p.flash, 0, r + 0.002, z);             // the team's stripes on top
  // the stock: one slab with the grip and the guard cut through, the battery cassette slotted in behind
  slab(g, [sh + 0.02, -r + 0.01, sh + 0.42, -r + 0.01, sh + 0.42, -r - 0.05, sh + 0.32, -r - 0.07, sh + 0.32, -r - 0.24, sh + 0.25, -r - 0.24, sh + 0.2, -r - 0.07, sh + 0.02, -r - 0.05], 0.06, HD, 0, 0.008,
    [[sh + 0.29, -r - 0.075, sh + 0.38, -r - 0.075, sh + 0.33, -r - 0.13, sh + 0.3, -r - 0.13]]);
  slab(g, [sh - 0.16, -r - 0.005, sh + 0.02, -r - 0.005, sh + 0.02, -r - 0.09, sh - 0.12, -r - 0.09], 0.075, '#c9a43a', 0, 0.006);   // the battery cassette (yellow: it is spent and swapped)
  // the fore-grip under the front
  slab(g, [sh + 0.66, -r + 0.01, sh + 0.74, -r + 0.01, sh + 0.73, -r - 0.17, sh + 0.67, -r - 0.17], 0.045, HD, 0, 0.006);
  // the optic block on the head's side, on its mount, its screen to the eye (the screen is the lamp; `rearSight` there),
  // its objective ahead (`frontSight`, empty: nothing folds)
  const ox = -r - 0.07, oy = r * 0.2;
  box(g, 0.08, 0.03, 0.05, HD, -r - 0.025, oy - 0.02, sh + 0.2);                                              // the mount
  slab(g, [sh + 0.06, oy - 0.04, sh + 0.3, oy - 0.04, sh + 0.3, oy + 0.04, sh + 0.24, oy + 0.06, sh + 0.06, oy + 0.06], 0.07, HD, ox, 0.008);
  cyl(g, 0.026, 0.01, '#20303a', ox, oy + 0.01, sh + 0.305, 8);                                               // the objective's glass
  const lamp = lampMesh(g, 0.05, 0.05, ox, oy + 0.01, sh + 0.056);
  const rearSight = rearAt(g, ox, oy + 0.01, sh + 0.05);
  const sight = new THREE.Group(); sight.position.set(ox, oy + 0.01, sh + 0.31); g.add(sight);
  // the antenna (one of the `folds`): a flat panel on a hinge, leant out
  const antenna = new THREE.Group(); antenna.position.set(-r * 0.8, r * 0.6, half - 0.3); g.add(antenna);
  const lean = new THREE.Group(); lean.rotation.z = 0.55; antenna.add(lean);
  plate(lean, [-0.05, 0, 0.05, 0, 0.05, 0.1, -0.05, 0.1], 0.008, HD, 0, [[-0.04, 0.01, 0.04, 0.01, 0.04, 0.09, -0.04, 0.09]]);
  box(lean, 0.084, 0.004, 0.003, p.flash, 0, 0.05, 0);
  const muzzle = at(g, 0, 0, half + 0.01), breech = at(g, 0, 0, -half - 0.01);
  const gripR = at(g, 0, -r - 0.16, sh + 0.285), gripL = at(g, 0, -r - 0.12, sh + 0.7);
  return finish(g, 'C', { muzzle, breech, gripR, gripL, frontSight: sight, rearSight, antenna, lamp }, bake);
}

/** Build the launcher in `option` (the owner's pick, C, by default). `bake: false` leaves its static parts unbaked (for a caller that bakes the whole). */
export function buildAntiDropshipMissile(p: UnitPalette, option: AntiDropshipMissileOption = ANTI_DROPSHIP_MISSILE_PICK.launcher, opts: { bake?: boolean } = {}): AntiDropshipMissileModel {
  const bake = opts.bake ?? true;
  return option === 'A' ? antiDropshipMissileA(p, bake) : option === 'B' ? antiDropshipMissileB(p, bake) : antiDropshipMissileC(p, bake);
}

/**
 * Carried (0) to raised (1): the front sight and the antenna fold back flat onto the tube as it is carried; both stand up
 * as it comes to the shoulder (late in the raise, as the LAW's sights spring up). The tube's slot on the person calls
 * `setLawExtended`, which moves the same (the antenna as one of its `folds`; the empty inner, covers and rear sight too).
 */
export function setAntiDropshipMissileRaised(m: AntiDropshipMissileModel, k: number): void { setLawExtended(m, k); }

/** The lamp at the eye: `t` seconds (any clock) blinks the track. */
export function setAntiDropshipMissileLock(m: AntiDropshipMissileModel, state: LockState, t = 0): void {
  const c = state === 'track' ? (Math.floor(t / LOCK_LAMP.blink) % 2 === 0 ? LOCK_LAMP.track : LOCK_LAMP.search) : LOCK_LAMP[state];
  (m.lamp.material as THREE.MeshBasicMaterial).color.set(c);
}

// ---- the missile -----------------------------------------------------------------------------------------------------

/**
 * The flight's beats as drawn (seconds from the launch; lane F's sim owns the real numbers — the look reads them when
 * they exist): the launch motor burns out inside the tube and the missile leaves at `ejectSpeed` (people a second) by
 * `eject`; the tail fins spring out over `fins`; it coasts clear of the gunner until the flight motor lights at
 * `ignite` (a flash `flash` long), which burns for `burn`; then it coasts dark. The look's `ejectDrop` is how far it
 * sags under the coast (people), for the sheet's script.
 */
export const ANTI_DROPSHIP_MISSILE_FLIGHT = { eject: 0.05, ejectSpeed: 24, fins: [0.05, 0.12], ignite: 0.3, flash: 0.06, burn: 2.0, ejectDrop: 0.25 } as const;

export interface MissileModel {
  group: THREE.Group;
  /** the tail fins on their hinges (folded flat round the body in the tube) */
  fins: THREE.Group[];
  /** the motor's flame (two cones), and its glow point that holds a few pixels at any range */
  flame: THREE.Group;
  glow: THREE.Points;
  length: number;
}

/** The missile: the body along +z from its middle, its nose at length/2. Its static parts bake (by material); the fins, the flame and the glow apart. */
export function buildMissile(p: UnitPalette, option: AntiDropshipMissileOption = ANTI_DROPSHIP_MISSILE_PICK.launcher): MissileModel {
  const g = new THREE.Group(), r = option === 'C' ? 0.04 : 0.034, L = option === 'B' ? 1.42 : 1.36, half = L / 2;
  const body = option === 'A' ? '#6d7158' : option === 'B' ? '#56633f' : '#8d9294', dark = option === 'C' ? '#3a3e40' : '#2f322a';
  cyl(g, r, L - 0.12, body, 0, 0, -0.04, 8);
  cyl(g, r + 0.002, 0.04, '#c9a83a', 0, 0, half - 0.3, 8, r + 0.002, true);                                  // the warhead's band (yellow: HE)
  cyl(g, r + 0.002, 0.03, '#7a5a34', 0, 0, -half + 0.3, 8, r + 0.002, true);                                 // the motor's band (brown)
  cyl(g, 0.022, 0.05, dark, 0, 0, -half + 0.005, 8, r);                                                     // the nozzle's taper
  if (option === 'A') cyl(g, r, 0.09, '#526070', 0, 0, half - 0.055, 6, 0.004);                              // the seeker's faceted dome
  else if (option === 'B') {
    const dome = new THREE.Mesh(new THREE.SphereGeometry(r, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), GUN_PRIMS.mat('#52606c'));
    dome.rotation.x = Math.PI / 2; dome.position.z = half - 0.1; g.add(dome);
    cyl(g, 0.003, 0.09, dark, 0, 0, half - 0.03, 4, 0.0005);                                                 // the needle ahead of the dome
    for (let i = 0; i < 3; i++) { const a = (i * 2 * Math.PI) / 3; box(g, 0.003, 0.003, 0.05, dark, Math.cos(a) * 0.012, Math.sin(a) * 0.012, half - 0.07); }   // its tripod
  } else {
    cyl(g, r, 0.06, '#20303a', 0, 0, half - 0.09, 8, r * 0.75);                                               // the seeker's window
    cyl(g, r * 0.75, 0.02, p.flash, 0, 0, half - 0.05, 8, r * 0.5);                                           //   its ring in the team's colour
  }
  for (let i = 0; i < 4; i++) {                                                                            // the canards
    const a = i * Math.PI / 2, w = i % 2 === 0 ? 0.05 : 0.035;
    const c = box(g, 0.004, w, 0.05, dark, 0, 0, half - 0.22); c.rotation.z = a; c.translateY(r + w / 2 - 0.004);
  }
  const fins: THREE.Group[] = [];
  for (let i = 0; i < 4; i++) {                                                                            // the tail fins, each on its root line
    const hinge = new THREE.Group(); hinge.rotation.z = Math.PI / 4 + i * Math.PI / 2; hinge.position.z = -half + 0.1; g.add(hinge);
    const fin = new THREE.Group(); fin.position.y = r; hinge.add(fin);
    slab(fin, [-0.07, 0, 0.03, 0, 0.0, 0.07, -0.07, 0.07], 0.004, dark);
    fins.push(fin);
  }
  const flame = new THREE.Group(); flame.position.z = -half; g.add(flame);
  const add = THREE.AdditiveBlending;
  const outer = new THREE.Mesh(new THREE.ConeGeometry(0.04, 0.42, 8, 1, true), markShared(new THREE.MeshBasicMaterial({ color: '#ff9a3a', transparent: true, opacity: 0.7, blending: add, depthWrite: false })));
  outer.rotation.x = -Math.PI / 2; outer.position.z = -0.21; flame.add(outer);
  const core = new THREE.Mesh(new THREE.ConeGeometry(0.022, 0.2, 6, 1, true), markShared(new THREE.MeshBasicMaterial({ color: '#fff4d0', blending: add, depthWrite: false, transparent: true })));
  core.rotation.x = -Math.PI / 2; core.position.z = -0.1; flame.add(core);
  const glowGeo = new THREE.BufferGeometry(); glowGeo.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, -0.06], 3));
  const glow = new THREE.Points(glowGeo, markShared(new THREE.PointsMaterial({ color: '#ffe0a0', size: 5, sizeAttenuation: false, transparent: true, blending: add, depthWrite: false })));
  flame.add(glow);
  bakeStaticParts(g, { exclude: [flame, ...fins] });
  const m: MissileModel = { group: g, fins, flame, glow, length: L };
  poseMissile(m, 0);
  return m;
}

/**
 * The missile at `age` seconds from the launch (ANTI_DROPSHIP_MISSILE_FLIGHT, or the sim's own beats in `beats`): the fins folded in
 * the tube, out after the eject; the flame and the glow from the ignite (a brighter flash first) through the burn,
 * flickering by `age`.
 */
export function poseMissile(m: MissileModel, age: number, beats: { ignite?: number; burn?: number } = {}): void {
  const F = ANTI_DROPSHIP_MISSILE_FLIGHT, ignite = beats.ignite ?? F.ignite, burn = beats.burn ?? F.burn;
  const open = smoothstep(F.fins[0], F.fins[1], age);
  for (const f of m.fins) f.rotation.z = -1.45 * (1 - open);
  const lit = age >= ignite && age < ignite + burn;
  m.flame.visible = lit;
  if (lit) {
    const since = age - ignite, flash = since < F.flash ? 1.8 - 0.8 * (since / F.flash) : 1;
    const flick = 0.88 + 0.12 * Math.sin(age * 91) * Math.sin(age * 37);
    m.flame.scale.set(flash, flash, flash * flick * Math.min(1, 0.3 + since / 0.08));
  }
}

// ---- the smoke ---------------------------------------------------------------------------------------------------

/** The ribbon's look: `width` at the nozzle → `spread` after `grow` seconds, fading from `peak` over `life`; a point each `every` seconds at most; `max` points. */
export interface TrailLook { width: number; spread: number; grow: number; life: number; peak: number; hot: number; every: number; max: number; color: string }
export const TRAIL_LOOK: TrailLook = { width: 0.14, spread: 1.3, grow: 2.5, life: 3.2, peak: 0.7, hot: 0.05, every: 1 / 60, max: 200, color: '#e2e0da' };

const _tan = new THREE.Vector3(), _view = new THREE.Vector3(), _side = new THREE.Vector3(), _p = new THREE.Vector3();

/**
 * A missile's smoke trail: a ribbon through the points it has passed, turned to face the camera each frame, widening and
 * fading with each point's age — one draw, `max` points (200: 3.3 s at 60 fps). `push` while the motor burns; `update`
 * every frame (the trail outlives the missile until its last point fades).
 */
export class MissileTrail {
  readonly mesh: THREE.Mesh;
  private readonly pts: Float32Array;
  private readonly born: Float64Array;
  private head = 0;
  private count = 0;
  private last = -Infinity;
  private readonly pos: THREE.BufferAttribute;
  private readonly col: THREE.BufferAttribute;
  private readonly tint = new THREE.Color();
  constructor(private readonly look: TrailLook = TRAIL_LOOK) {
    const n = look.max;
    this.pts = new Float32Array(n * 3); this.born = new Float64Array(n);
    const g = new THREE.BufferGeometry();
    this.pos = new THREE.BufferAttribute(new Float32Array(n * 2 * 3), 3); this.pos.setUsage(THREE.DynamicDrawUsage);
    this.col = new THREE.BufferAttribute(new Float32Array(n * 2 * 4), 4); this.col.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', this.pos); g.setAttribute('color', this.col);
    const idx: number[] = [];
    for (let i = 0; i < n - 1; i++) { const a = 2 * i; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
    g.setIndex(idx);
    g.setDrawRange(0, 0);
    this.mesh = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, depthWrite: false, side: THREE.DoubleSide }));
    this.mesh.frustumCulled = false;
    this.tint.set(look.color);
  }
  /** The missile passed `at` at time `t` (seconds, any clock that `update` shares). */
  push(at: THREE.Vector3, t: number): void {
    if (t - this.last < this.look.every && this.count > 0) { this.pts.set([at.x, at.y, at.z], this.head * 3); return; }   // (the newest point follows the missile between)
    this.last = t;
    this.head = (this.head + 1) % this.look.max;
    this.pts.set([at.x, at.y, at.z], this.head * 3); this.born[this.head] = t;
    this.count = Math.min(this.look.max, this.count + 1);
  }
  /** Whether any of it is still showing at `t`. */
  alive(t: number): boolean { return this.count > 0 && t - this.born[this.head] < this.look.life; }   // (the newest point fades last)
  clear(): void { this.count = 0; this.last = -Infinity; this.mesh.geometry.setDrawRange(0, 0); }
  /** Rebuild the ribbon facing `camera` at time `t`: newest point first. */
  update(camera: THREE.Camera, t: number): void {
    const L = this.look, n = L.max, P = this.pts, pos = this.pos.array as Float32Array, col = this.col.array as Float32Array;
    let k = 0;
    for (let j = 0; j < this.count; j++) {
      const i = (this.head - j + n) % n, age = t - this.born[i];
      if (age > L.life) break;
      const ia = (this.head - Math.max(0, j - 1) + n) % n, ib = (this.head - Math.min(this.count - 1, j + 1) + n) % n;
      _tan.set(P[ia * 3] - P[ib * 3], P[ia * 3 + 1] - P[ib * 3 + 1], P[ia * 3 + 2] - P[ib * 3 + 2]);
      _p.set(P[i * 3], P[i * 3 + 1], P[i * 3 + 2]);
      _view.copy(camera.position).sub(_p);
      _side.crossVectors(_tan, _view);
      const sl = _side.length();
      if (sl > 1e-9) _side.multiplyScalar(1 / sl); else _side.set(0, 1, 0);
      const w = (L.width + (L.spread - L.width) * Math.min(1, Math.sqrt(age / L.grow))) / 2;
      const fade = 1 - age / L.life, a = L.peak * fade * fade * Math.min(1, j / 2);
      const hot = Math.max(0, 1 - age / L.hot);   // the newest few: white-hot at the flame
      for (const s of [-1, 1]) {
        const v = 2 * k + (s > 0 ? 1 : 0);
        pos[v * 3] = _p.x + _side.x * w * s; pos[v * 3 + 1] = _p.y + _side.y * w * s; pos[v * 3 + 2] = _p.z + _side.z * w * s;
        col[v * 4] = this.tint.r + (1 - this.tint.r) * hot; col[v * 4 + 1] = this.tint.g + (0.95 - this.tint.g) * hot; col[v * 4 + 2] = this.tint.b + (0.8 - this.tint.b) * hot; col[v * 4 + 3] = a;
      }
      k++;
    }
    this.pos.needsUpdate = true; this.col.needsUpdate = true;
    this.mesh.geometry.setDrawRange(0, Math.max(0, k - 1) * 6);
  }
}

/** One puff: where, when, how big at first, how much it swells (×), how long it lasts, and its drift (people a second). */
interface Puff { x: number; y: number; z: number; born: number; size: number; grow: number; life: number; dx: number; dy: number; dz: number }
/**
 * Puffs, every one in the scene in one draw (an instanced low icosahedron, lit, half see-through): each swells as it
 * drifts and shrinks away at its end. The back-blast and the eject's smoke; trail B's chain. `max` at once (the oldest
 * goes first).
 */
export class PuffField {
  readonly mesh: THREE.InstancedMesh;
  private puffs: Puff[] = [];
  private readonly m4 = new THREE.Matrix4();
  constructor(readonly max = 256, color = '#e4e2dc', opacity = 0.38) {
    this.mesh = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(1, 1), new THREE.MeshLambertMaterial({ color, transparent: true, opacity, depthWrite: false }), max);
    this.mesh.count = 0; this.mesh.frustumCulled = false; this.mesh.name = 'antiDropshipMissile:puffs';
  }
  add(at: THREE.Vector3, t: number, o: { size: number; grow?: number; life?: number; drift?: THREE.Vector3 }): void {
    if (this.puffs.length >= this.max) this.puffs.shift();
    const d = o.drift;
    this.puffs.push({ x: at.x, y: at.y, z: at.z, born: t, size: o.size, grow: o.grow ?? 3, life: o.life ?? 1.4, dx: d?.x ?? 0, dy: d?.y ?? 0, dz: d?.z ?? 0 });
  }
  /**
   * The launch's two blasts: the back-blast out of the `breech` along `back` (a cone of a dozen puffs, fast and wide)
   * and the eject's small puff at the `muzzle`. Directions are unit vectors; sizes in people.
   */
  launch(muzzle: THREE.Vector3, breech: THREE.Vector3, back: THREE.Vector3, t: number, rnd: () => number = Math.random): void {
    for (let i = 0; i < 18; i++) {
      const s = 2 + 6 * rnd();
      const d = new THREE.Vector3(back.x + (rnd() - 0.5) * 0.6, back.y + (rnd() - 0.5) * 0.6, back.z + (rnd() - 0.5) * 0.6).normalize().multiplyScalar(s);
      this.add(breech, t, { size: 0.07 + 0.06 * rnd(), grow: 2.5 + 2 * rnd(), life: 1.0 + 0.8 * rnd(), drift: d });
    }
    for (let i = 0; i < 4; i++) this.add(muzzle, t, { size: 0.04 + 0.03 * rnd(), grow: 2.5, life: 0.8, drift: new THREE.Vector3((rnd() - 0.5) * 0.6, 0.3 + 0.3 * rnd(), (rnd() - 0.5) * 0.6) });
  }
  clear(): void { this.puffs = []; this.mesh.count = 0; }
  update(t: number): void {
    this.puffs = this.puffs.filter(p => t - p.born < p.life);
    this.puffs.forEach((p, i) => {
      const age = Math.max(0, t - p.born), f = age / p.life, slow = 1 - Math.exp(-age * 2.5);   // the drift slows as it spreads
      const s = p.size * (1 + p.grow * Math.sqrt(f)) * (f > 0.7 ? 1 - (f - 0.7) / 0.3 : 1);
      this.m4.makeScale(s, s, s).setPosition(p.x + p.dx * slow / 2.5, p.y + p.dy * slow / 2.5, p.z + p.dz * slow / 2.5);
      this.mesh.setMatrixAt(i, this.m4);
    });
    this.mesh.count = this.puffs.length;
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}

function smoothstep(a: number, b: number, x: number): number { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); }
